import {
  Assessment,
  GetAssessmentSummaryResponse,
  GroupSettings,
} from '@sparx/api/apis/sparx/assessment/v1/assessment';
import { Timestamp } from '@sparx/api/google/protobuf/timestamp';
import { max } from 'date-fns';

import { AssessmentsOverviewCardProps } from './AssessmentCard';

/**
 * Determine if an assessment is valid based on the validFrom and validUntil dates. An
 * assessment is valid if the current time is before the invalidUntil date and after the
 * validFrom date, if they exist.
 * @param the assessment to check
 */
const assessmentIsValid = (assessment: Assessment) => {
  const now = new Date();

  // If the validFrom date is set and is in the future, the assessment is invalid as we're too early
  if (assessment.validFrom && now < Timestamp.toDate(assessment.validFrom)) {
    return false;
  }

  // If the validUntil date is set and is in the past, the assessment is invalid as we're too late
  if (assessment.validUntil && now > Timestamp.toDate(assessment.validUntil)) {
    return false;
  }

  return true;
};

/**
 * Create the props for the cards for the assessments overview page from the summary
 * We create one card per assessment, except if we have a revision task for an assessment group, we create a separate
 * card which will be given the title of the assessment group.
 * @param summary the assessment summary response from the server
 */
export const getCards = (
  summary?: GetAssessmentSummaryResponse,
): AssessmentsOverviewCardProps[] => {
  const cards: AssessmentsOverviewCardProps[] = [];

  if (!summary) {
    return cards;
  }

  summary.assessments.forEach(a => {
    // Skip assessments which are not valid
    if (!assessmentIsValid(a)) {
      return;
    }

    const fixUpTask = summary.fixUpTasks.find(t => t.assessmentName === a.name);
    const revisionTask = summary.revisionTasks.find(t => t.assessmentName === a.name);
    const groupSettings = summary.groupSettings.find(t => t.assessmentName === a.name);

    const sortDate = getSortDate(groupSettings);

    // If we have a revision task which is for an assessment group, create a separate card for the assessment group:
    if (revisionTask && revisionTask.assessmentGroupName) {
      const assessmentGroup = summary.assessmentGroups.find(
        g => g.name === revisionTask.assessmentGroupName,
      );
      if (assessmentGroup) {
        cards.push({ assessment: a, assessmentGroup, revisionTask, sortDate });
      }
    }

    // If we have a fix up task, or a revision which is not for an assessment group, create a card for the assessment:
    if (fixUpTask || (revisionTask && !revisionTask.assessmentGroupName)) {
      cards.push({
        assessment: a,
        fixUpTask,
        revisionTask: revisionTask?.assessmentGroupName ? undefined : revisionTask,
        sortDate,
      });
    }
  });

  cards.sort(sortByMostRecent);

  return cards;
};

export const getSortDate = (groupSettings?: GroupSettings): Date => {
  const fixupDate = groupSettings?.settings?.fixUpStartedAt;
  const revisionDate = groupSettings?.settings?.revisionStartedAt;

  // Use the latest of the dates the fixup or revision task was started
  // Default missing times to the Unix epoch.
  const sortDate = max([
    fixupDate ? Timestamp.toDate(fixupDate) : new Date(0),
    revisionDate ? Timestamp.toDate(revisionDate) : new Date(0),
  ]);

  return sortDate;
};

/** Sort the cards by the date at which the fix up or revision task was started */
export const sortByMostRecent = (
  a: AssessmentsOverviewCardProps,
  b: AssessmentsOverviewCardProps,
): number => {
  const dateSortVal = b.sortDate.valueOf() - a.sortDate.valueOf();
  const aName = a.assessmentGroup?.displayName ?? a.assessment.displayName;
  const bName = b.assessmentGroup?.displayName ?? b.assessment.displayName;
  return dateSortVal || aName.toLocaleLowerCase().localeCompare(bName.toLocaleLowerCase());
};
